import { useEffect } from 'react';

import { FormAwareCheckbox } from '@/components/form/formAwareInputs/FormAwareCheckbox';
import { FormModal } from '@/components/modals/FormModal/FormModal';
import { FormModalActions } from '@/components/modals/FormModal/FormModalActions';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { PopperContent } from '@/components/poppers/PopperContent';
import { FormProvider, useForm } from '@/components/react-hook-form';
import { useReportError } from '@/hooks/useReportError';
import { PathsOf } from '@/types/subform';

import { useUpdateDocumentSummaryPromptMutation } from '../graphql/AdminPromptIterationPage.generated';

interface AdvancedSettingsForm {
  useExtendedThinkingModel: boolean;
}

type AdvancedSettingsFormPaths = PathsOf<AdvancedSettingsForm>;

interface DocumentSummaryPromptAdvancedSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  promptId: string;
  currentValue: boolean;
}

export function DocumentSummaryPromptAdvancedSettingsModal({
  isOpen,
  onClose,
  promptId,
  currentValue,
}: DocumentSummaryPromptAdvancedSettingsModalProps) {
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();

  const formMethods = useForm<AdvancedSettingsForm>({
    defaultValues: {
      useExtendedThinkingModel: currentValue,
    },
  });

  const { handleSubmit, formState, control, reset } = formMethods;

  useEffect(() => {
    if (isOpen) {
      reset({ useExtendedThinkingModel: currentValue });
    }
  }, [isOpen, currentValue, reset]);

  const [updatePrompt, { loading }] = useUpdateDocumentSummaryPromptMutation({
    onCompleted: () => {
      showFeedback('Successfully updated advanced settings', {
        variant: 'success',
      });
      onClose();
    },
    onError: (err) => {
      showFeedback('Failed to update advanced settings');
      reportError('Failed to update prompt advanced settings', err);
    },
  });

  const onSubmit = handleSubmit(async (formData) => {
    await updatePrompt({
      variables: {
        input: {
          id: promptId,
          update: {
            useExtendedThinkingModal: formData.useExtendedThinkingModel,
          },
        },
      },
    });
  });

  return (
    <FormProvider {...formMethods}>
      <FormModal
        isOpen={isOpen}
        heading="Advanced settings"
        onClose={onClose}
        actions={
          <FormModalActions.Provider formState={formState}>
            <FormModalActions.CancelButton onClick={onClose} />
            <FormModalActions.SaveButton onClick={onSubmit} disabled={loading}>
              Save
            </FormModalActions.SaveButton>
          </FormModalActions.Provider>
        }
      >
        <FormAwareCheckbox<AdvancedSettingsForm>
          control={control}
          fieldName={
            'useExtendedThinkingModel' as const satisfies AdvancedSettingsFormPaths
          }
          label="Use extended thinking model"
          contextualHelp={
            <PopperContent body="Use a smarter but slower model to generate the summaries. This is useful when you are seeing poor results for extremely detailed prompts. The smarter model is currently GPT-5." />
          }
        />
      </FormModal>
    </FormProvider>
  );
}
